import {Pipe, PipeTransform} from '@angular/core';
import length from '@turf/length';
import along from '@turf/along';
import cleanCoords from '@turf/clean-coords';
import lineSlice from '@turf/line-slice';
import {lineString} from '@turf/helpers';
import {Entity} from '../classes/entity';
import {ConvertGeometryPipe} from './convert-geometry.pipe';

@Pipe({
    name: 'dashedLine'
})
export class DashedLinePipe implements PipeTransform {

    static entityLineMap = new Map<number, { lat: number, lng: number }[][]>();

    transform(entity: Entity): { lat: number, lng: number }[][] {
        if (entity.attr?.linetype !== 'Continuous') {
            const linetypeMaps = new Map<string, {
                dashLength: number,
                gapLength: number,
            }>([
                ['39strp', {dashLength: 3, gapLength: 9}],
                ['93strp', {dashLength: 9, gapLength: 3}],
                ['11strp', {dashLength: 1, gapLength: 1}],
                ['13strp', {dashLength: 1, gapLength: 3}],
                ['91strp', {dashLength: 9, gapLength: 1}],
                ['markering 0.5-0.5 Streep', {dashLength: 0.5, gapLength: 0.5}],
                ['markering 0.50-0.50 Streep', {dashLength: 0.5, gapLength: 0.5}],
                ['markering 0.60-0.60 Streep', {dashLength: 0.6, gapLength: 0.6}],
                ['markering 1-1 Streep', {dashLength: 1, gapLength: 1}],
                ['1-1_streep', {dashLength: 1, gapLength: 1}],
                ['markering 1-3 Streep', {dashLength: 1, gapLength: 3}],
                ['1-3_streep', {dashLength: 1, gapLength: 3}],
                ['markering 3-1 Streep', {dashLength: 3, gapLength: 1}],
                ['3-1_streep', {dashLength: 3, gapLength: 1}],
                ['markering 3-9 Streep', {dashLength: 3, gapLength: 9}],
                ['3-9_streep', {dashLength: 3, gapLength: 9}],
                ['markering 9-1 Streep', {dashLength: 9, gapLength: 1}],
                ['9-1_streep', {dashLength: 9, gapLength: 1}],
                ['markering 9-3 Streep', {dashLength: 9, gapLength: 3}],
                ['9-3_streep', {dashLength: 9, gapLength: 3}],
                ['markering 30-270 Streep', {dashLength: 0.30, gapLength: 0.270}],
                ['markering 270-30 Streep', {dashLength: 0.270, gapLength: 0.30}],
                ['markering ononderbroken', {dashLength: 1, gapLength: 0}]
            ]);

            const coordinates = entity.line.coordinates;
            const linetype = linetypeMaps.get(entity.attr.linetype);
            if (!linetype || linetype.gapLength === 0) {
                return [(new ConvertGeometryPipe()).transform(entity.line)];
            }
            if (DashedLinePipe.entityLineMap.has(entity.handle)) {
                return DashedLinePipe.entityLineMap.get(entity.handle);
            }
            const dashLength = linetype.dashLength;
            const gapLength = linetype.gapLength;

            const line = cleanCoords(lineString(coordinates));
            const totalLength = length(line, {units: 'kilometers'});
            const dashLenKm = dashLength / 1000;
            const gapLenKm = gapLength / 1000;

            const visibleSegments: { lat: number, lng: number }[][] = [];
            let cursor = 0;
            while (cursor < totalLength) {
                const dashStart = cursor;
                const dashEnd = Math.min(cursor + dashLenKm, totalLength);
                const startPoint = along(line, dashStart, {units: 'kilometers'});
                const endPoint = along(line, dashEnd, {units: 'kilometers'});
                const segment = lineSlice(startPoint, endPoint, line);
                const segmentLatLng = segment.geometry.coordinates.map(([lng, lat]) => ({lat, lng}));
                visibleSegments.push(segmentLatLng);
                cursor += dashLenKm + gapLenKm;
            }
            DashedLinePipe.entityLineMap.set(entity.handle, visibleSegments);
            return visibleSegments;
        }
        return [(new ConvertGeometryPipe()).transform(entity.line)];
    }
}
